import React, { createContext, useCallback, useContext, useMemo, useState } from 'react';
import { Image } from 'antd';
import { buildGallery, VIEWER_GROUP_ATTR, type ViewerItem } from './imageViewerUtils';

// 261030 — «bấm ảnh để xem lớn» dùng chung cho luồng 4 bước. Một trình xem (antd Image.PreviewGroup, điều khiển từ ngoài) đặt ở gốc màn:
// ảnh đầy đủ không cắt, phóng to/thu nhỏ/xoay, ←/→ chuyển giữa các ảnh CÙNG NHÓM theo thứ tự trên màn, Esc đóng.
// Mỗi ảnh chỉ cần `<ViewableImg group="…">`; nhóm = mọi `img[data-viewer-group="…"]` đang có trên trang ⇒ không phải truyền danh sách qua từng component.

type OpenFn = (group: string, el: HTMLImageElement) => void;
const ViewerContext = createContext<OpenFn | null>(null);

export const ImageViewerProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [state, setState] = useState<{ items: ViewerItem[]; current: number } | null>(null);
  const open = useCallback<OpenFn>((group, el) => {
    const nodes = Array.from(document.querySelectorAll<HTMLImageElement>(`img[${VIEWER_GROUP_ATTR}="${group}"]`));
    const g = buildGallery(nodes.map((n) => ({ src: n.getAttribute('src'), alt: n.getAttribute('alt') })), nodes.indexOf(el));
    if (g) {
      // Esc: tiêu điểm còn nằm trong Drawer/Modal bên dưới thì chính nó nhận Esc trước và tự đóng, lightbox (nghe ở window) ở lại ⇒ bỏ tiêu điểm khỏi ảnh vừa bấm.
      (document.activeElement as HTMLElement | null)?.blur?.();
      setState(g);
    }
  }, []);
  const items = useMemo(() => (state?.items ?? []).map((i) => ({ src: i.src, alt: i.alt })), [state]);
  return <ViewerContext.Provider value={open}>
    {children}
    {/* Chỉ dựng khi đang xem: không giữ cây preview ẩn cho mọi màn, đóng = gỡ hẳn. */}
    {state ? <Image.PreviewGroup items={items} preview={{
      open: true, current: state.current,
      onChange: (current: number) => setState((old) => (old ? { ...old, current } : old)),
      onOpenChange: (visible: boolean) => { if (!visible) setState(null); },
    }} /> : null}
  </ViewerContext.Provider>;
};

/**
 * `<img>` bấm được để xem lớn. Ngoài `ImageViewerProvider` (màn cũ, test) ⇒ vẫn là `<img>` thường, không con trỏ tay, không gắn nhóm.
 * Chỉ chính ảnh nhận cú bấm (nút / menu đè trên thẻ là phần tử khác) và cú bấm không nổi bọt lên thẻ cha.
 */
export const ViewableImg: React.FC<React.ImgHTMLAttributes<HTMLImageElement> & { group: string; /** nằm trong một nút khác (vd. mục danh sách): không thêm role/tab — bàn phím dùng nút bọc */ nested?: boolean }> = ({ group, nested, className, onClick, ...rest }) => {
  const open = useContext(ViewerContext);
  const alt = rest.alt ?? '';
  if (!open || !rest.src) return <img {...rest} alt={alt} className={className} onClick={onClick} />;
  return <img {...rest} alt={alt} {...{ [VIEWER_GROUP_ATTR]: group }} className={`${className ? `${className} ` : ''}wf4-viewable`} {...(nested ? {} : { role: 'button', tabIndex: 0, 'aria-label': rest.alt ? `Xem lớn: ${rest.alt}` : 'Xem ảnh lớn' })}
    onClick={(e) => { e.stopPropagation(); onClick?.(e); open(group, e.currentTarget); }}
    onKeyDown={nested ? undefined : (e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); e.stopPropagation(); open(group, e.currentTarget); } }} />;
};
